import React, { useEffect, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Users, ArrowRight, Check, X, Shield, Droplet, StickyNote, Trash2 } from "lucide-react";

const SOURCE_BADGE = {
  justspas: { label: "JustSpas", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  bioguard: { label: "BioGuard", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  "": { label: "In-store", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const addr = (a) => [a?.street, a?.suburb, a?.state, a?.postcode].filter(Boolean).join(", ") || "—";

function Side({ c, tag, busy, onKeep, onDelete }) {
  const sb = SOURCE_BADGE[c.source] || SOURCE_BADGE[""];
  return (
    <div className="flex-1 rounded-xl border-2 border-slate-200 bg-white p-3 flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <span className="font-display font-bold text-slate-900 truncate">{c.name || "—"}</span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${sb.cls} shrink-0`}>{sb.label}</span>
      </div>
      <div className="text-[12px] text-slate-500 mt-1 space-y-0.5">
        <div>{c.phone || "no phone"}</div>
        <div className="truncate">{c.email || "no email"}</div>
        <div className="truncate">{addr(c.address)}</div>
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {c.has_legacy && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 inline-flex items-center gap-1"><Shield className="h-3 w-3" />15-yr history</span>}
        {c.bodies > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 inline-flex items-center gap-1"><Droplet className="h-3 w-3" />{c.bodies} pool{c.bodies > 1 ? "s" : ""}</span>}
        {c.notes > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 inline-flex items-center gap-1"><StickyNote className="h-3 w-3" />{c.notes} notes</span>}
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={onKeep} disabled={busy} data-testid={`dup-keep-${tag}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[13px] font-bold px-3 py-2 disabled:opacity-50"><Check className="h-4 w-4" /> Keep</button>
        <button onClick={onDelete} disabled={busy} data-testid={`dup-delete-${tag}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-red-300 text-red-600 hover:bg-red-50 text-[13px] font-bold px-3 py-2 disabled:opacity-50"><Trash2 className="h-4 w-4" /> Delete</button>
      </div>
    </div>
  );
}

export default function Duplicates() {
  const [pairs, setPairs] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api("GET", "/client-duplicates?limit=200").then((d) => {
      setPairs(d.pairs); setTotal(d.total);
    }).catch(() => setPairs([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const key = (p) => `${p.a.id}|${p.b.id}`;
  const dismiss = (i) => setPairs((ps) => ps.filter((_, idx) => idx !== i));

  const merge = async (p, i) => {
    const keep = p.suggested_keep || p.a.id;
    const remove = keep === p.a.id ? p.b.id : p.a.id;
    setBusy(key(p));
    try {
      await api("POST", "/client-merge", { keep_id: keep, remove_id: remove });
      toast.success(`Merged into ${keep === p.a.id ? p.a.name : p.b.name}`);
      dismiss(i);
    } catch (e) { toast.error(e.message || "Merge failed"); }
    setBusy(null);
  };
  const keepBoth = async (p, i) => {
    try { await api("POST", "/duplicate-ignore", { a_id: p.a.id, b_id: p.b.id }); toast.success("Kept as separate records"); dismiss(i); }
    catch (e) { toast.error(e.message || "Failed"); }
  };
  const deleteOne = async (p, i, who) => {
    if (!window.confirm(`Delete "${who.name}" permanently?`)) return;
    setBusy(key(p));
    try { await api("DELETE", `/clients/${who.id}`); toast.success(`Deleted ${who.name}`); dismiss(i); }
    catch (e) { toast.error(e.message || "Delete failed"); }
    setBusy(null);
  };

  return (
    <div data-testid="duplicates-panel">
      <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Users className="h-6 w-6 text-sky-600" /> Possible Duplicates</h1>
      <p className="text-sm text-slate-500 mb-5">Same customer appearing twice (often JustSpas + BioGuard). On each record hit <b>Keep</b> (they're different people — stop showing this pair) or <b>Delete</b> (remove that record). If they're the same customer, hit <b>Merge</b> underneath to combine them into one. Deletions are logged and recoverable.</p>

      {loading ? (
        <p className="text-slate-400 text-sm">Scanning {`${total || ""}`} records…</p>
      ) : pairs.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center" data-testid="dup-empty">
          <Check className="h-10 w-10 mx-auto text-emerald-400 mb-2" />
          <p className="font-display font-bold text-slate-700">No more duplicates to review — nice and clean.</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-3" data-testid="dup-count">{total.toLocaleString()} possible duplicate pairs found · showing {pairs.length}</p>
          <div className="space-y-3">
            {pairs.map((p, i) => (
              <div key={key(p)} data-testid="dup-pair" className="rounded-2xl border-2 border-slate-200 bg-white p-3">
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  {p.reasons.map((r) => <span key={r} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">{r}</span>)}
                  <span className="text-[11px] text-slate-400 ml-auto">match strength {p.score}</span>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch gap-2">
                  <Side c={p.a} tag="a" busy={busy === key(p)} onKeep={() => keepBoth(p, i)} onDelete={() => deleteOne(p, i, p.a)} />
                  <div className="flex sm:flex-col items-center justify-center text-slate-300"><ArrowRight className="h-5 w-5" /></div>
                  <Side c={p.b} tag="b" busy={busy === key(p)} onKeep={() => keepBoth(p, i)} onDelete={() => deleteOne(p, i, p.b)} />
                </div>
                <button onClick={() => merge(p, i)} disabled={busy === key(p)} data-testid="dup-merge-btn"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2.5 disabled:opacity-50">
                  <Check className="h-4 w-4" /> {busy === key(p) ? "Working…" : `Merge — same customer, combine into ${(p.suggested_keep === p.b.id ? p.b.name : p.a.name) || "one record"}`}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
